import _ from 'lodash'
import moment, { Moment } from 'moment'
import {
  Category,
  OpeningHoursPeriod,
  RestaurantCategorySchedule,
  RestaurantOpeningHours,
  ScheduleCategory,
} from './types'

export const TODAY_HIGHLIGHT_COLOR = '#e0f7fa'

export const DATE_HEADER_FORMAT = 'D.M.YYYY'

export const WEEKDAYS: { [day in keyof RestaurantOpeningHours]: { translationKey: string } } = {
  MON: { translationKey: 'monday' },
  TUE: { translationKey: 'tuesday' },
  WED: { translationKey: 'wednesday' },
  THU: { translationKey: 'thursday' },
  FRI: { translationKey: 'friday' },
  SAT: { translationKey: 'saturday' },
  SUN: { translationKey: 'sunday' },
}

export function dayNumberToWeekDay(dayNumber: number): string {
  switch (dayNumber) {
    case 0:
      return 'SUN'
    case 1:
      return 'MON'
    case 2:
      return 'TUE'
    case 3:
      return 'WED'
    case 4:
      return 'THU'
    case 5:
      return 'FRI'
    case 6:
      return 'SAT'
    default:
      return 'MON'
  }
}

export function weekdayToTranslateKey(weekday: keyof RestaurantOpeningHours): string {
  switch (weekday) {
    case 'MON':
      return 'restaurantMenu.monday'
    case 'TUE':
      return 'restaurantMenu.tuesday'
    case 'WED':
      return 'restaurantMenu.wednesday'
    case 'THU':
      return 'restaurantMenu.thursday'
    case 'FRI':
      return 'restaurantMenu.friday'
    case 'SAT':
      return 'restaurantMenu.saturday'
    case 'SUN':
      return 'restaurantMenu.sunday'
    default:
      return 'restaurantMenu.monday'
  }
}

export const DEFAULT_OPENING_HOURS_FOR_EXPRESS_CHECKOUT: RestaurantOpeningHours = {
  MON: { opens: '00:00', closes: '23:59' },
  TUE: { opens: '00:00', closes: '23:59' },
  WED: { opens: '00:00', closes: '23:59' },
  THU: { opens: '00:00', closes: '23:59' },
  FRI: { opens: '00:00', closes: '23:59' },
  SAT: { opens: '00:00', closes: '23:59' },
  SUN: { opens: '00:00', closes: '23:59' },
}

export function sortCategories(categories: Category[]): Category[] {
  return _.sortBy(categories, [(category) => getCategorySortKey(category)])
}

export function sortScheduleCategories(categories: ScheduleCategory[]): ScheduleCategory[] {
  return _.sortBy(categories, [(category) => getCategorySortKey(category)])
}

export function getCategorySortKey(category: Category | ScheduleCategory): string {
  return category.name.toLowerCase().replace(/à/g, 'a')
}

export function getDateHeaders(startDate: Moment, endDate: Moment): string[] {
  const dateHeaders: string[] = []
  let tempDate = startDate.clone()
  while (tempDate.isSameOrBefore(endDate)) {
    dateHeaders.push(tempDate.format(DATE_HEADER_FORMAT))
    tempDate.add(1, 'day')
  }
  return dateHeaders
}

export function getOpeningHoursForDate(
  date: Moment,
  category: Category,
  schedules?: RestaurantCategorySchedule[],
  periods?: OpeningHoursPeriod[],
): { schedules: { opens: string; closes: string }[] | null; isExceptional: boolean; isDefaultTime: boolean } {
  const exceptionalSchedules = schedules?.filter((schedule: RestaurantCategorySchedule) => {
    const scheduleStart = moment(schedule.startDate, 'YYYY-MM-DD').startOf('day')
    const scheduleEnd = moment(schedule.endDate, 'YYYY-MM-DD').endOf('day')
    return date.isBetween(scheduleStart, scheduleEnd, 'day', '[]')
  })
  const exceptionalCategories = exceptionalSchedules?.flatMap((schedule) => schedule.categories) ?? []
  const exceptionalCategory = exceptionalCategories.find((c) => c.id === category.id)
  const { opens, closes, schedules: exceptionalCategorySchedules } = exceptionalCategory ?? {}
  const exceptionalSchedulesForDay = exceptionalCategorySchedules ?? (opens && closes ? [{ opens, closes }] : null)
  if (exceptionalCategory) {
    return {
      schedules: _.sortBy(exceptionalSchedulesForDay, [(schedule) => schedule.opens]),
      isExceptional: true,
      isDefaultTime: false,
    }
  }
  const period = periods?.find((period) => {
    const periodStart = moment(period.startDate, 'YYYY-MM-DD').startOf('day')
    const periodEnd = moment(period.endDate, 'YYYY-MM-DD').endOf('day')
    return period.categoryId === category.id && date.isBetween(periodStart, periodEnd, 'day', '[]')
  })
  if (period) {
    const periodDataForDay = period.openingHours[dayNumberToWeekDay(date.day())]
    const schedulesForDay = periodDataForDay?.schedules ?? [
      { opens: periodDataForDay.opens, closes: periodDataForDay.closes },
    ]
    return {
      schedules: _.sortBy(schedulesForDay, [(schedule) => schedule.opens]),
      isExceptional: false,
      isDefaultTime: false,
    }
  }
  const defaultHours = category.defaultOpeningHours[dayNumberToWeekDay(date.day())]
  return {
    schedules: [{ opens: defaultHours?.opens ?? '-', closes: defaultHours?.closes ?? '*' }],
    isExceptional: false,
    isDefaultTime: true,
  }
}
